/**
 * Hook tự động xác định permKey dựa vào URL hiện tại
 * và trả về canEdit / canView cho trang đó.
 */
import { useLocation } from 'react-router-dom'
import { usePermission } from './usePermission'

// Order: most-specific prefix first → fallback parent → module-level catch-all
// last. `Array.find` returns first match, nên thứ tự quyết định resolve.
// `usePermission` tự fallback sub→parent ("purchase.local_p" → "purchase") nếu
// permissions map chưa có sub-permKey.
const ROUTE_PERM_MAP: Array<{ prefix: string; permKey: string }> = [
  // Standalone alias route cho PurchaseContractPage shell (App.tsx `/purchase-contract`).
  // Boundary-match KHÔNG còn cho nó mượn `/purchase` → phải map riêng kẻo fail-open.
  { prefix: '/purchase-contract', permKey: 'purchase.local_p' },
  // Purchase Contract tabs (PurchaseContractPage shell, /purchase/contract/<tab>)
  { prefix: '/purchase/contract/list', permKey: 'purchase.local_p' },
  { prefix: '/purchase/contract/forward-pu', permKey: 'purchase.forward' },
  { prefix: '/purchase/contract/npe-contract', permKey: 'purchase.npe' },
  { prefix: '/purchase/contract/amendment', permKey: 'purchase.annex' },
  { prefix: '/purchase/contract/ptbf-fixation', permKey: 'purchase.ptbf' },
  { prefix: '/purchase/contract/npe-fixation', permKey: 'purchase.npe_fix_alloc' },
  { prefix: '/purchase/contract/npe-allocation', permKey: 'purchase.npe_fix_alloc' },
  { prefix: '/purchase/contract/ptbf-rolling', permKey: 'purchase.ptbf' },
  { prefix: '/purchase/contract/references', permKey: 'purchase.refs' },
  { prefix: '/purchase/contract/plans', permKey: 'purchase.local_p' },
  { prefix: '/purchase/contract', permKey: 'purchase.local_p' },
  // Purchase synced standalone routes (App.tsx — /purchase/<segment> không qua
  // PurchaseContractPage shell). Nav menu không link tới đây nhưng row click,
  // wizard, deep link đều navigate vào. Phải gate cùng permKey với tab tương ứng.
  { prefix: '/purchase/local-p', permKey: 'purchase.local_p' },
  { prefix: '/purchase/contracts', permKey: 'purchase' },
  { prefix: '/purchase/npe-contracts', permKey: 'purchase.npe' },
  { prefix: '/purchase/annexes', permKey: 'purchase.annex' },
  { prefix: '/purchase/ptbf-fixations', permKey: 'purchase.ptbf' },
  { prefix: '/purchase/ptbf-rollings', permKey: 'purchase.ptbf' },
  { prefix: '/purchase/npe-fixations', permKey: 'purchase.npe_fix_alloc' },
  { prefix: '/purchase/npe-allocations', permKey: 'purchase.npe_fix_alloc' },
  { prefix: '/purchase/forward', permKey: 'purchase.forward' },
  { prefix: '/purchase/plans', permKey: 'purchase.local_p' },
  { prefix: '/purchase/refs', permKey: 'purchase.refs' },
  // Procurement screen (/purchase/control/*) — gate bằng function `procurement`
  // (navigation.ts permKey). PHẢI đặt TRƯỚC catch-all `/purchase` bên dưới, nếu
  // không `Array.find` khớp `/purchase` trước → shadow → gate nhầm bằng `purchase`.
  // KHÔNG đụng `/purchase/contract` (đã khớp ở các prefix cụ thể phía trên).
  { prefix: '/purchase/control', permKey: 'procurement' },
  // Catch-all cho purchase route mới — gate bằng `purchase` parent. Phải đặt
  // SAU tất cả prefix cụ thể vì Array.find trả first match.
  { prefix: '/purchase', permKey: 'purchase' },
  { prefix: '/vendors', permKey: 'purchase.vendor' },
  { prefix: '/sales/contract', permKey: 'saleContract' },
  { prefix: '/sales/control', permKey: 'saleControl' },
  { prefix: '/futures', permKey: 'futures' },
  { prefix: '/report', permKey: 'position' }, // RT008 — Report/Commercial Position
  // Factory: URL đã bỏ prefix `/factory/` (2026-05-28) → factory_code lên thẳng
  // top-level segment. Mỗi factory có permKey riêng (khớp navigation.ts). PHẢI
  // map từng code, nếu không guard không tìm thấy permKey → fail-open → URL bypass.
  { prefix: '/baoloc', permKey: 'factoryBl' },
  { prefix: '/nhontrach', permKey: 'factoryNt' },
  { prefix: '/nguyencuong', permKey: 'factoryNc' },
  { prefix: '/nongsan', permKey: 'factoryNs' },
  { prefix: '/pacorini', permKey: 'factoryPaco' },
  { prefix: '/factory', permKey: 'factory' },
  { prefix: '/matching', permKey: 'matchingPs' },
  { prefix: '/quality', permKey: 'quality' },
  { prefix: '/virtual-warehouse', permKey: 'virtualWarehouse' },
  // Master Data leaf pages: gate bằng ĐÚNG backend Function của từng ref (không
  // có Function `masterData` trong permissions map → `masterData` luôn resolve
  // NoGrant → canEdit=false → nút New/Edit/Delete bị mờ). Map theo service `fn`:
  //   Calendar/Month/Holiday → purchase.refs.calendar (MarketCalendar/Month/HolidayService)
  //   Counter Party Bank / Counter Party → purchase.vendor (MasterDataCpBankService / Vendor)
  //   còn lại                → purchase.refs          (các ref CRUD chung)
  { prefix: '/master-data/calendar', permKey: 'purchase.refs.calendar' },
  { prefix: '/master-data/month', permKey: 'purchase.refs.calendar' },
  { prefix: '/master-data/holiday', permKey: 'purchase.refs.calendar' },
  { prefix: '/master-data/counter-party-bank', permKey: 'purchase.vendor' },
  { prefix: '/master-data/counter-party', permKey: 'purchase.vendor' },
  { prefix: '/master-data', permKey: 'purchase.refs' },
  { prefix: '/settings', permKey: 'setting' },
]

/**
 * Boundary-aware prefix match: chỉ khớp khi pathname === prefix HOẶC bắt đầu
 * bằng `prefix + '/'`. Tránh substring-match lỏng của `startsWith` thuần (vd
 * `/purchase-contract`.startsWith(`/purchase`) hay `/purchase/contracts`
 * .startsWith(`/purchase/contract`)) → resolve nhầm permKey của module khác.
 */
function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + '/')
}

export function usePagePermission() {
  const { pathname } = useLocation()
  const permKey = ROUTE_PERM_MAP.find((m) => pathMatchesPrefix(pathname, m.prefix))?.permKey
  return usePermission(permKey)
}
